import { Link } from "react-router-dom";
import { BookOpen, Heart, Target, Eye, Sparkles, CheckCircle2 } from "lucide-react";
import BottomNav from "@/components/site/BottomNav";
import { LOGO_URL } from "@/lib/brand";

const BELIEFS = [
  "The Bible is the inspired Word of God — our final authority for faith and life.",
  "Jesus Christ is the Son of God, our Savior, Lord, and soon-returning King.",
  "Salvation is by grace through faith in Christ alone — a gift, not a wage.",
  "The Holy Spirit indwells, comforts, and empowers every believer to abide.",
  "The Sabbath is a sacred gift — a weekly invitation to rest in our Creator.",
  "Prayer is real conversation with a real God who hears and answers.",
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO — portrait-like, soft */}
      <section className="page-hero" style={{ backgroundColor: "var(--navy-800)" }}>
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(115deg, var(--navy-900), var(--navy-700) 60%, var(--gold-500))",
          }}
        />
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-[0.32em] text-gold-light mb-4">Our Story</div>
            <h1 className="font-display text-4xl lg:text-6xl text-cream leading-tight">
              A small voice. <span className="italic text-gold-light">A faithful God.</span>
            </h1>
            <p className="mt-5 text-cream/85 text-lg leading-relaxed max-w-xl">
              Abide Daily Ministry began with a simple longing — to help people meet Jesus in the quiet moments of an ordinary day.
              No noise. No agenda. Just Scripture, prayer, and the slow work of abiding.
            </p>
          </div>
          <div className="lg:col-span-2 flex justify-center">
            <div className="relative">
              <div
                className="absolute -inset-6 rounded-full blur-3xl opacity-50"
                style={{ background: "radial-gradient(circle, rgba(217,184,100,0.55), transparent 70%)" }}
              />
              <div className="relative w-72 h-72 flex items-center justify-center drop-shadow-2xl">
                <img src={LOGO_URL} alt="Abide Daily emblem" className="w-full h-full object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION + VISION two columns */}
      <section className="py-20 lg:py-24 bg-cream">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl p-9 border border-cream-200 lift">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: "var(--navy-800)", color: "var(--gold-400)" }}>
              <Target size={22} />
            </div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-2">Mission</div>
            <h2 className="font-display text-3xl text-navy-deep">Why we exist</h2>
            <p className="mt-4 text-navy/75 leading-relaxed">
              To help every weary heart pause, listen, and learn the quiet rhythm of abiding in Jesus — through daily Scripture,
              prayer, and Christ-centered teaching.
            </p>
          </div>
          <div className="bg-white rounded-2xl p-9 border border-cream-200 lift">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: "var(--gold-500)", color: "var(--navy-900)" }}>
              <Eye size={22} />
            </div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-2">Vision</div>
            <h2 className="font-display text-3xl text-navy-deep">Where we are going</h2>
            <p className="mt-4 text-navy/75 leading-relaxed">
              A growing family of believers around the world who have learned to come home to Christ each day — and who carry that
              quiet, abiding peace into a noisy world.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE BELIEVE — list with serif numbers */}
      <section className="py-20 lg:py-24" style={{ backgroundColor: "var(--cream-100)" }}>
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-12">
            <div className="ornament mb-4">
              <span className="text-xs uppercase tracking-[0.32em] text-gold font-medium">What We Believe</span>
            </div>
            <h2 className="font-display text-3xl lg:text-4xl text-navy-deep">Held by ancient truths.</h2>
          </div>
          <ol className="space-y-5">
            {BELIEFS.map((b, i) => (
              <li
                key={i}
                data-testid={`belief-${i + 1}`}
                className="bg-white rounded-xl p-6 border border-cream-200 flex gap-5 items-start"
              >
                <span className="serif-num shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <div className="flex-1">
                  <p className="text-navy-deep leading-relaxed">{b}</p>
                </div>
                <CheckCircle2 className="text-gold shrink-0" size={22} />
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* HOW WE HELP */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-3">How We Help</div>
            <h2 className="font-display text-3xl lg:text-5xl text-navy-deep leading-tight">
              Practical ways we walk alongside you.
            </h2>
            <p className="mt-5 text-navy/75 leading-relaxed text-lg">
              Abide Daily is not a brand — it's a quiet companion for the journey. Here is how we serve.
            </p>
          </div>
          <div className="space-y-4">
            {[
              { Icon: BookOpen, title: "Daily Devotionals", desc: "A short, Christ-centered reading for every morning." },
              { Icon: Heart, title: "Prayer Support", desc: "Submit a request — we will pray with you in faith." },
              { Icon: Sparkles, title: "Bible Promises", desc: "Scripture organized for the seasons of your soul." },
            ].map(({ Icon, title, desc }, i) => (
              <div key={i} className="flex gap-4 p-5 rounded-xl bg-cream-50 border border-cream-200">
                <span className="w-12 h-12 shrink-0 rounded-xl flex items-center justify-center" style={{ backgroundColor: "var(--navy-800)", color: "var(--gold-400)" }}>
                  <Icon size={20} />
                </span>
                <div>
                  <h4 className="font-display text-lg text-navy-deep">{title}</h4>
                  <p className="text-sm text-navy/70 mt-1">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING VERSE */}
      <section className="py-16 lg:py-20" style={{ backgroundColor: "var(--navy-900)" }}>
        <div className="max-w-3xl mx-auto px-5 text-center text-cream">
          <p className="scripture text-2xl lg:text-3xl leading-relaxed">
            "He that abideth in me, and I in him, the same bringeth forth much fruit: for without me ye can do nothing."
          </p>
          <p className="mt-4 text-gold-light text-xs tracking-[0.3em] uppercase">— John 15:5</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/devotionals" data-testid="about-cta-devotional" className="btn-gold px-6 py-3 rounded-full">Start with Today's Devotional</Link>
            <Link to="/prayer-requests" data-testid="about-cta-prayer" className="px-6 py-3 rounded-full border border-cream/40 text-cream hover:bg-cream hover:text-navy-deep transition-all">Share a Prayer Request</Link>
          </div>
        </div>
      </section>

      <BottomNav exclude={["/about"]} />
    </div>
  );
}
